import { useEffect, useRef, type RefObject } from 'react';
import { focusElementOrFirst, getTabbable } from '../components/base/primitiveUtils';

/**
 * Keeps keyboard focus inside `ref` while `active` (a modal dialog, the site sidebar).
 *
 * - On activation focus moves to `initialFocus`, else the first tabbable element,
 *   else the container itself.
 * - Tab from the last element wraps to the first; Shift+Tab from the first wraps to
 *   the last.
 * - Focus that escapes by other means (a click behind an overlay, a screen-reader
 *   jump) is pulled back in. Popovers opened from inside the trap are portalled to
 *   <body>, so anything inside a `[data-popover]` panel counts as inside.
 * - On deactivation focus returns to whatever had it before (or `returnFocus`).
 */

export interface UseFocusTrapOptions {
  /** Element to focus on activation. */
  initialFocus?: RefObject<HTMLElement | null>;
  /** true (default): restore the previously focused element. A ref: focus that instead. false: leave focus alone. */
  returnFocus?: boolean | RefObject<HTMLElement | null>;
}

const POPOVER_SELECTOR = '[data-popover]';

export function useFocusTrap(
  ref: RefObject<HTMLElement | null>,
  active: boolean,
  { initialFocus, returnFocus = true }: UseFocusTrapOptions = {},
): void {
  const optionsRef = useRef({ initialFocus, returnFocus });
  useEffect(() => {
    optionsRef.current = { initialFocus, returnFocus };
  });

  useEffect(() => {
    const container = ref.current;
    if (!active || !container || typeof document === 'undefined') return undefined;

    const previous = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    const isInside = (node: EventTarget | null) =>
      node instanceof Node &&
      (container.contains(node) ||
        (node instanceof Element && node.closest(POPOVER_SELECTOR) !== null));

    const focusInitial = () => {
      const target = optionsRef.current.initialFocus?.current;
      if (target && focusElementOrFirst(target)) return;
      const first = getTabbable(container)[0];
      if (first) {
        first.focus({ preventScroll: true });
        return;
      }
      if (!container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
      container.focus({ preventScroll: true });
    };

    if (!isInside(document.activeElement)) focusInitial();

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Tab' || event.defaultPrevented) return;
      const items = getTabbable(container);
      if (items.length === 0) {
        event.preventDefault();
        container.focus({ preventScroll: true });
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      const current = document.activeElement;
      // Focus inside a portalled popover: let the popover's own Tab handling run.
      if (current instanceof Element && current.closest(POPOVER_SELECTOR) && !container.contains(current)) return;
      if (event.shiftKey) {
        if (current === first || !container.contains(current)) {
          event.preventDefault();
          last.focus({ preventScroll: true });
        }
      } else if (current === last || !container.contains(current)) {
        event.preventDefault();
        first.focus({ preventScroll: true });
      }
    };

    const onFocusIn = (event: FocusEvent) => {
      if (isInside(event.target)) return;
      focusInitial();
    };

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('focusin', onFocusIn);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('focusin', onFocusIn);
      const back = optionsRef.current.returnFocus;
      if (back === false) return;
      const target = back === true ? previous : back.current;
      // Only to something still on the page.
      if (target && target.isConnected) target.focus({ preventScroll: true });
    };
  }, [active, ref]);
}

export default useFocusTrap;
